/**
 * HistogramChart — frequency distribution of concentration values.
 * Native hand-rolled SVG (no Recharts). Vertical regulatory MCL lines
 * (concentration axis = x). Rectangle zoom + dblclick reset via
 * useChartView. No time-range row since x is concentration, not time.
 */

import { useState, useMemo, useRef, useEffect, useCallback, useId } from 'react';
import { scaleLinear } from 'd3-scale';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { AnalyteSelectorPanel } from '../../AnalyteSelectorPanel';
import { ANALYTE_COLORS } from '../../chartTheme';
import { ChartTooltip } from '../../ChartTooltip';
import { useChartView } from '../../interactions/useChartView';
import { useRegulatoryLines } from '../../../hooks/useRegulatoryLines';
import { defaultHiddenAnalytes } from '../../chartDefaults';
import { ChartViewResetChip } from '../../ChartViewResetChip';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

interface Bin {
  binStart: number;
  binEnd: number;
  count: number;
}

function computeBins(values: number[], binCount = 20): Bin[] {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const binWidth = range / binCount;
  const bins: Bin[] = [];
  for (let i = 0; i < binCount; i++) {
    bins.push({
      binStart: min + i * binWidth,
      binEnd: min + (i + 1) * binWidth,
      count: 0,
    });
  }
  for (const v of values) {
    let idx = Math.floor((v - min) / binWidth);
    if (idx >= binCount) idx = binCount - 1;
    if (idx < 0) idx = 0;
    bins[idx].count++;
  }
  return bins;
}

const MARGIN = { top: 12, right: 24, bottom: 40, left: 60 };
const CHART_HEIGHT = 420;

export function HistogramChart({ data, unit }: Props) {
  const [hiddenAnalytes, setHiddenAnalytes] = useState<Set<string> | null>(() => {
    const s = defaultHiddenAnalytes(data);
    return s.size > 0 ? s : null;
  });

  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound], i) => ({
      code,
      compound,
      color: ANALYTE_COLORS[i % ANALYTE_COLORS.length],
    }));
  }, [data.wells]);

  const effectiveHidden = useMemo(() => {
    if (hiddenAnalytes !== null) return hiddenAnalytes;
    return new Set<string>();
  }, [hiddenAnalytes]);

  const visibleAnalyteCodes = useMemo(
    () => new Set(allAnalytes.filter((a) => !effectiveHidden.has(a.code)).map((a) => a.code)),
    [allAnalytes, effectiveHidden],
  );

  const activeCode = useMemo(() => {
    const codes = Array.from(visibleAnalyteCodes);
    return codes.length > 0 ? codes[0] : null;
  }, [visibleAnalyteCodes]);

  const { lines: regulatoryLines } = useRegulatoryLines(data, visibleAnalyteCodes);

  const bins = useMemo(() => {
    if (!activeCode) return [];
    const values: number[] = [];
    for (const well of data.wells) {
      const analyte = well.analytes[activeCode];
      if (!analyte) continue;
      for (const m of analyte.measurements) {
        const v = convertConc(m.concentration, unit);
        if (v !== null) values.push(v);
      }
    }
    return computeBins(values);
  }, [data.wells, activeCode, unit]);

  const analyteColor = useMemo(() => {
    if (!activeCode) return ANALYTE_COLORS[0];
    const idx = allAnalytes.findIndex((a) => a.code === activeCode);
    return idx >= 0 ? ANALYTE_COLORS[idx % ANALYTE_COLORS.length] : ANALYTE_COLORS[0];
  }, [activeCode, allAnalytes]);

  // Sizing
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const clipId = `hist-clip-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  // Base domains
  const baseXDomain = useMemo<readonly [number, number]>(() => {
    if (bins.length === 0) return [0, 1];
    let lo = bins[0].binStart;
    let hi = bins[bins.length - 1].binEnd;
    for (const line of regulatoryLines) {
      if (line.value > hi) hi = line.value;
    }
    return [lo, hi];
  }, [bins, regulatoryLines]);

  const baseYDomain = useMemo<readonly [number, number]>(() => {
    let yMax = 0;
    for (const b of bins) if (b.count > yMax) yMax = b.count;
    return [0, yMax > 0 ? yMax * 1.1 : 1];
  }, [bins]);

  // View hook
  const xPxToDataRef = useRef<(px: number) => number>(() => 0);
  const yPxToDataRef = useRef<(py: number) => number>(() => 0);
  const view = useChartView(
    svgRef,
    (px) => xPxToDataRef.current(px),
    (py) => yPxToDataRef.current(py),
    {
      baseXDomain,
      baseYDomain,
      dragZoomAxes: 'x',
      chartArea: {
        top: MARGIN.top,
        bottom: MARGIN.top + innerHeight,
        left: MARGIN.left,
        right: MARGIN.left + innerWidth,
      },
    },
  );

  const xScale = useMemo(
    () => scaleLinear().domain([view.xDomain[0], view.xDomain[1]]).range([MARGIN.left, MARGIN.left + innerWidth]),
    [view.xDomain, innerWidth],
  );
  const yScale = useMemo(
    () => scaleLinear().domain([view.yDomain[0], view.yDomain[1]]).range([MARGIN.top + innerHeight, MARGIN.top]).nice(),
    [view.yDomain, innerHeight],
  );
  xPxToDataRef.current = (px: number) => xScale.invert(px);
  yPxToDataRef.current = (py: number) => yScale.invert(py);

  const xTicks = useMemo(() => xScale.ticks(8), [xScale]);
  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  const formatY = (v: number) => (Number.isInteger(v) ? v.toFixed(0) : v.toFixed(0));
  const formatX = (v: number) => {
    const abs = Math.abs(v);
    if (v === 0) return '0';
    if (abs < 0.01) return v.toExponential(1);
    if (abs < 1) return v.toPrecision(2);
    return v.toFixed(2);
  };

  // Hover
  interface HoverState { bin: Bin; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current; if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      // Find which bin contains the pixel.
      const dataX = xScale.invert(localX);
      let foundBin: Bin | null = null;
      for (const b of bins) {
        if (dataX >= b.binStart && dataX <= b.binEnd) {
          foundBin = b;
          break;
        }
      }
      if (foundBin && foundBin.count > 0) {
        setHover({ bin: foundBin, clientX: e.clientX, clientY: e.clientY });
      } else {
        setHover(null);
      }
    },
    [bins, xScale],
  );
  const handleMouseLeave = useCallback(() => setHover(null), []);

  // Empty state
  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  const activeName = activeCode ? allAnalytes.find((a) => a.code === activeCode)?.compound : '';
  const totalSamples = bins.reduce((s, b) => s + b.count, 0);

  return (
    <div className="space-y-3">
      <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
        Histogram showing the frequency distribution of {activeName?.toLowerCase() || 'concentration'} values
        across {totalSamples} samples. The MCL reference line(s) help identify what proportion of samples exceed regulatory limits.
      </p>

      <AnalyteSelectorPanel
        analytes={allAnalytes}
        visibleCodes={visibleAnalyteCodes}
        onToggle={(code) => {
          const next = new Set(allAnalytes.filter((a) => a.code !== code).map((a) => a.code));
          setHiddenAnalytes(next);
        }}
        onSelectAll={() => setHiddenAnalytes(new Set())}
        onDeselectAll={() => setHiddenAnalytes(new Set(allAnalytes.map((a) => a.code)))}
      />

      <ChartViewResetChip isZoomed={view.isZoomed} onReset={view.reset} />

      <div ref={containerRef} role="img" aria-label="Histogram of concentration frequency distribution">
        {bins.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
            SELECT AN ANALYTE TO DISPLAY
          </div>
        ) : (
          <svg
            ref={svgRef}
            width={width || '100%'}
            height={CHART_HEIGHT}
            style={{ display: 'block', userSelect: 'none', touchAction: 'none', cursor: 'crosshair' }}
          >
            <defs>
              <clipPath id={clipId}>
                <rect x={MARGIN.left} y={MARGIN.top} width={innerWidth} height={innerHeight} />
              </clipPath>
            </defs>

            <g clipPath={`url(#${clipId})`}>
              {/* Gridlines */}
              {yTicks.map((t) => {
                const y = yScale(t);
                return (
                  <line
                    key={`grid-${t}`}
                    x1={MARGIN.left}
                    x2={MARGIN.left + innerWidth}
                    y1={y}
                    y2={y}
                    stroke="var(--dv-chart-grid)"
                    strokeDasharray="3 3"
                    strokeWidth={1}
                    shapeRendering="crispEdges"
                  />
                );
              })}

              {/* Bars */}
              {bins.map((b, i) => {
                const x0 = xScale(b.binStart);
                const x1 = xScale(b.binEnd);
                const y = yScale(b.count);
                const yBase = yScale(0);
                const isHovered = hover?.bin === b;
                return (
                  <rect
                    key={`bar-${i}`}
                    x={x0 + 1}
                    y={y}
                    width={Math.max(0, x1 - x0 - 2)}
                    height={Math.max(0, yBase - y)}
                    fill={analyteColor}
                    opacity={isHovered ? 1 : 0.85}
                    rx={2}
                  />
                );
              })}

              {/* Regulatory limit lines — VERTICAL (concentration on x-axis) */}
              {regulatoryLines.map((line) => {
                const v = convertConc(line.value, unit);
                if (v === null) return null;
                const [xLo, xHi] = view.xDomain;
                if (v > xHi || v < xLo) return null;
                const x = xScale(v);
                const dashArray = line.style === 'dashed' ? '8 4' : line.style === 'dotted' ? '2 4' : undefined;
                return (
                  <g key={line.key} style={{ filter: 'url(#dv-mcl-glow)' }}>
                    <line
                      x1={x}
                      x2={x}
                      y1={MARGIN.top}
                      y2={MARGIN.top + innerHeight}
                      stroke={line.color}
                      strokeWidth={1}
                      strokeDasharray={dashArray}
                    />
                    <text
                      x={x + 4}
                      y={MARGIN.top + 12}
                      fontSize={9}
                      fill={line.color}
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {`${line.label} ${line.analyteName}`}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Y-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <g key={`yt-${t}`}>
                  <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatY(t)}
                  </text>
                </g>
              );
            })}
            <text transform={`translate(${MARGIN.left - 44}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
              Frequency
            </text>

            {/* X-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {xTicks.map((t, i) => {
              const x = xScale(t);
              return (
                <g key={`xt-${i}`}>
                  <line x1={x} x2={x} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight + 4} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={x} y={MARGIN.top + innerHeight + 16} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatX(t)}
                  </text>
                </g>
              );
            })}
            <text x={MARGIN.left + innerWidth / 2} y={MARGIN.top + innerHeight + 32} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
              Concentration (mg/L)
            </text>

            {/* Rect-zoom preview */}
            {view.rectPreview && (
              <rect
                x={view.rectPreview.x0}
                y={view.rectPreview.y0}
                width={view.rectPreview.x1 - view.rectPreview.x0}
                height={view.rectPreview.y1 - view.rectPreview.y0}
                fill="rgba(16, 185, 129, 0.12)"
                stroke="var(--dv-accent-emerald, #10b981)"
                strokeWidth={1}
                strokeDasharray="3 3"
                pointerEvents="none"
              />
            )}

            {/* Hit overlay */}
            <rect
              x={MARGIN.left}
              y={MARGIN.top}
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            />
          </svg>
        )}
      </div>

      {hover && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 160, fontSize: 11 }}>
            <div style={{ fontSize: 10, color: 'var(--dv-tooltip-label, rgba(255,255,255,0.7))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              {hover.bin.binStart.toFixed(3)}–{hover.bin.binEnd.toFixed(3)} mg/L
            </div>
            <div style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)', fontWeight: 600 }}>
              {hover.bin.count} sample{hover.bin.count === 1 ? '' : 's'}
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
